if (!customElements.get('bundle-save-section')) {
  customElements.define(
    'bundle-save-section',
    class BundleSaveSection extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.cart = {}; // { productId: { quantity, variantId, price, ... } }
        this.discountApplicationMode = this.dataset.discountApplication || 'percent';
        this.mainCartSyncVariantIds = new Set();
        this.mainCartSyncTimeout = null;
        
        // Define discount tiers
        this.discountTiers = {
          5: {
            code: '',
            bxgyCode: this.dataset.bxgyCodeTier5 || ''
          },
          10: {
            code: '',
            bxgyCode: this.dataset.bxgyCodeTier10 || ''
          }
        };

        this.init();
      }

      init() {
        this.readDiscountCodes();
        this.setupEventListeners();
        this.initMobileCartDrawer();
        this.loadCart();
        window.addEventListener('resize', () => this.updateDiscountProgress());
        window.addEventListener('resize', () => this.syncMobileCartStateForViewport());
        window.addEventListener('resize', () => this.updateMobileTopOffset());
        window.addEventListener('scroll', () => this.updateMobileTopOffset(), { passive: true });
        // Initial positioning
        requestAnimationFrame(() => {
          this.updateDiscountProgress();
          this.updateMobileTopOffset();
        });
      }

      initMobileCartDrawer() {
        const mobileToggle = this.querySelector('.bundle-cart-mobile-toggle');
        if (!mobileToggle) return;

        mobileToggle.addEventListener('click', () => {
          this.classList.toggle('mobile-cart-expanded');
          this.updateMobileTopOffset();
          this.syncMobileCartToggleLabel();
        });

        this.syncMobileCartStateForViewport();
      }

      syncMobileCartStateForViewport() {
        if (window.innerWidth > 990) {
          this.classList.remove('mobile-cart-expanded');
          this.style.removeProperty('--bundle-mobile-top-offset');
        } else {
          this.updateMobileTopOffset();
        }
        this.syncMobileCartToggleLabel();
      }

      syncMobileCartToggleLabel() {
        const mobileToggle = this.querySelector('.bundle-cart-mobile-toggle');
        if (!mobileToggle) return;

        const isExpanded = this.classList.contains('mobile-cart-expanded');
        mobileToggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        mobileToggle.setAttribute('aria-label', isExpanded ? 'Sepeti kapat' : 'Sepeti ac');
      }

      updateMobileTopOffset() {
        if (window.innerWidth > 990) return;

        const selectorList = [
          '.shopify-section-header',
          '.shopify-section-announcement-bar',
          '.announcement-bar',
          '#shopify-section-announcement-bar'
        ];

        const uniqueElements = new Set();
        let maxBottom = 0;

        selectorList.forEach((selector) => {
          document.querySelectorAll(selector).forEach((element) => {
            if (uniqueElements.has(element)) return;
            uniqueElements.add(element);

            const style = window.getComputedStyle(element);
            if (style.display === 'none' || style.visibility === 'hidden') return;

            const rect = element.getBoundingClientRect();
            if (rect.height <= 0) return;

            const isTopLayer = style.position === 'fixed' || style.position === 'sticky' || rect.top <= 0;
            if (isTopLayer) {
              maxBottom = Math.max(maxBottom, rect.bottom);
            }
          });
        });

        const fallbackTop = parseFloat(
          window.getComputedStyle(document.documentElement).getPropertyValue('--header-bottom-position')
        ) || 64;

        const offset = Math.max(maxBottom, fallbackTop) + 8;
        this.style.setProperty('--bundle-mobile-top-offset', `${Math.ceil(offset)}px`);
      }

      readDiscountCodes() {
        const tiers = this.querySelectorAll('.discount-tier');
        tiers.forEach(tier => {
          const tierNum = parseInt(tier.dataset.tier);
          const code = tier.dataset.code || '';
          const bxgyCode = tier.dataset.bxgyCode || '';
          if (this.discountTiers[tierNum]) {
            this.discountTiers[tierNum].code = code;
            this.discountTiers[tierNum].bxgyCode = bxgyCode || this.discountTiers[tierNum].bxgyCode || '';
          }
        });
      }

      setupEventListeners() {
        // Quantity input listeners (Bundle items)
        this.querySelectorAll('.quantity-input').forEach(input => {
          input.addEventListener('change', (e) => this.handleQuantityChange(e));
        });

        // Variant select listeners
        this.querySelectorAll('.bundle-product-variant-select').forEach(select => {
          select.addEventListener('change', (e) => {
            const productId = e.currentTarget.dataset.productId;
            this.updateProductVariant(productId);
          });
        });

        // Cart Remove listener (Sidebar)
        this.addEventListener('click', (e) => {
          if (e.target.closest('.cart-item-remove')) {
            const button = e.target.closest('.cart-item-remove');
            const productId = button.dataset.productId;
            this.removeProduct(productId);
          }
        });

        // Next/Checkout button listener
        const nextButton = this.querySelector(`#bundle-next-${this.sectionId}`);
        if (nextButton) {
          nextButton.addEventListener('click', () => this.handleNextClick());
        }

      }

      handleQuantityChange(e) {
        const inputEl = e.currentTarget;
        const productId = String(inputEl.dataset.productId);
        const quantity = parseInt(inputEl.value) || 0;
        
        const productCard = inputEl.closest('.bundle-product-card');
        if (!productCard) return;

        const variantSelect = productCard.querySelector('.bundle-product-variant-select');
        let variantId, variantPrice;

        if (variantSelect.tagName === 'SELECT') {
          const selectedOption = variantSelect.options[variantSelect.selectedIndex];
          variantId = variantSelect.value;
          variantPrice = parseFloat(selectedOption?.dataset.price || 0);
        } else {
          variantId = variantSelect.value;
          variantPrice = parseFloat(variantSelect.dataset.price || 0);
        }

        const productTitleEl = productCard.querySelector('.bundle-product-title a, .card-heading a');
        const productTitle = productTitleEl?.textContent?.trim() || 'Product';
        
        const productImageEl = productCard.querySelector('.bundle-product-image, .card-media img, .media img');
        let productImage = '';
        if (productImageEl) {
           productImage = productImageEl.src || productImageEl.getAttribute('src') || '';
        }

        this.updateCartItem(productId, quantity, variantId, variantPrice, productTitle, productImage);
      }

      updateCartItem(productId, quantity, variantId, variantPrice, productTitle, productImage) {
        const productIdStr = String(productId);
        quantity = Math.max(0, quantity);
        const existingItem = this.cart[productIdStr];
        const previousVariantId = existingItem?.variantId ? String(existingItem.variantId) : '';

        if (quantity === 0) {
          delete this.cart[productIdStr];
        } else {
          if (!this.cart[productIdStr]) this.cart[productIdStr] = {};
          this.cart[productIdStr] = {
            variantId,
            quantity,
            price: variantPrice,
            productId: productIdStr,
            productTitle,
            productImage
          };
          if (variantId) {
            this.mainCartSyncVariantIds.add(String(variantId));
          }
        }

        if (previousVariantId && previousVariantId !== String(variantId || '')) {
          this.mainCartSyncVariantIds.add(previousVariantId);
        }

        this.saveCart();
        this.updateDisplay();
        this.scheduleMainCartSync();
      }

      removeProduct(productId) {
        const productIdStr = String(productId);
        const removedItem = this.cart[productIdStr];
        if (removedItem?.variantId) {
          this.mainCartSyncVariantIds.add(String(removedItem.variantId));
        }
        delete this.cart[productIdStr];
        this.saveCart();
        
        // Reset inputs
        const input = this.querySelector(`.quantity-input[data-product-id="${productIdStr}"]`);
        if (input) input.value = 0;

        this.updateDisplay();
        this.scheduleMainCartSync();
      }

      updateProductVariant(productId) {
        const productIdStr = String(productId);
        if (this.cart[productIdStr]) {
             // Re-read variant info
             const productCard = this.querySelector(`.bundle-product-card[data-product-id="${productIdStr}"]`);
             if(productCard) {
                 const previousVariantId = this.cart[productIdStr].variantId
                   ? String(this.cart[productIdStr].variantId)
                   : '';
                 const variantSelect = productCard.querySelector('.bundle-product-variant-select');
                 let variantId, variantPrice;
                 if (variantSelect.tagName === 'SELECT') {
                    const selectedOption = variantSelect.options[variantSelect.selectedIndex];
                    variantId = variantSelect.value;
                    variantPrice = parseFloat(selectedOption?.dataset.price || 0);
                 } else {
                    variantId = variantSelect.value;
                    variantPrice = parseFloat(variantSelect.dataset.price || 0);
                 }
                 this.cart[productIdStr].variantId = variantId;
                 this.cart[productIdStr].price = variantPrice;
                 if (previousVariantId) this.mainCartSyncVariantIds.add(String(previousVariantId));
                 if (variantId) this.mainCartSyncVariantIds.add(String(variantId));
                 this.saveCart();
                 this.updateDisplay();
                 this.scheduleMainCartSync();
             }
        }
      }

      scheduleMainCartSync() {
        if (this.mainCartSyncTimeout) {
          clearTimeout(this.mainCartSyncTimeout);
        }

        this.mainCartSyncTimeout = setTimeout(() => {
          this.syncMainCartWithBundle().catch((error) => {
            console.error('Bundle main cart sync failed:', error);
          });
        }, 120);
      }

      getBundleVariantQuantities() {
        const quantities = {};
        Object.values(this.cart).forEach(item => {
          const variantId = String(item.variantId || '');
          if (!variantId) return;
          quantities[variantId] = (quantities[variantId] || 0) + item.quantity;
        });
        return quantities;
      }

      async syncMainCartWithBundle() {
        const bundleQuantities = this.getBundleVariantQuantities();
        const updates = {};

        this.mainCartSyncVariantIds.forEach((variantId) => {
          updates[variantId] = bundleQuantities[variantId] || 0;
        });

        Object.keys(bundleQuantities).forEach((variantId) => {
          updates[variantId] = bundleQuantities[variantId];
        });

        if (Object.keys(updates).length === 0) return;

        await fetch(window.routes?.cart_update_url || '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
      }

      saveCart() {
        localStorage.setItem(`bundle-cart-${this.sectionId}`, JSON.stringify(this.cart));
      }

      loadCart() {
        const savedCart = localStorage.getItem(`bundle-cart-${this.sectionId}`);
        if (savedCart) {
          try {
            this.cart = JSON.parse(savedCart);
          } catch (e) { this.cart = {}; }
        }
        this.syncInputsWithCart();
        this.updateDisplay();
      }

      syncInputsWithCart() {
        Object.values(this.cart).forEach(item => {
           const input = this.querySelector(`.quantity-input[data-product-id="${item.productId}"]`);
           if (input) input.value = item.quantity;
        });
      }

      getTotalItemCount() {
        return Object.values(this.cart).reduce((sum, item) => sum + item.quantity, 0);
      }

      getSubtotal() {
        return Object.values(this.cart).reduce((sum, item) => sum + (item.price * item.quantity), 0);
      }
      
      getBundleSubtotal() {
        return this.getSubtotal();
      }

      getDiscountPercentage() {
        const itemCount = this.getTotalItemCount();
        if (itemCount >= 10) return 0.07;
        if (itemCount >= 5) return 0.03;
        return 0;
      }
      
      getDiscountCode() {
        const itemCount = this.getTotalItemCount();
        if (itemCount >= 10) return this.discountTiers[10].code;
        if (itemCount >= 5) return this.discountTiers[5].code;
        return '';
      }

      getBxgyTierCode() {
        const itemCount = this.getTotalItemCount();
        if (itemCount >= 10) return this.discountTiers[10].bxgyCode;
        if (itemCount >= 5) return this.discountTiers[5].bxgyCode;
        return '';
      }

      getDiscountCodesForCheckout() {
        const discountCodes = [];

        if (this.discountApplicationMode === 'percent' || this.discountApplicationMode === 'both') {
          const percentCode = this.getDiscountCode();
          if (percentCode) discountCodes.push(percentCode);
        }

        if (this.discountApplicationMode === 'bxgy' || this.discountApplicationMode === 'both') {
          const tierBxgyCode = this.getBxgyTierCode();
          if (tierBxgyCode) discountCodes.push(tierBxgyCode);
        }

        return [...new Set(discountCodes.filter(Boolean))];
      }

      getCheckoutDiscountQuery() {
        const discountCodes = this.getDiscountCodesForCheckout();
        if (discountCodes.length === 0) return '';
        return discountCodes.join(',');
      }

      getTotalDiscount() {
        const bundleSubtotal = this.getBundleSubtotal();
        const discountPercent = this.getDiscountPercentage();
        return bundleSubtotal * discountPercent;
      }

      getTotal() {
        return this.getSubtotal() - this.getTotalDiscount();
      }

      updateDisplay() {
        const itemCount = this.getTotalItemCount();
        const subtotal = this.getSubtotal();
        const discount = this.getTotalDiscount();
        const total = this.getTotal();

        // Update Text
        const itemCountEl = this.querySelector(`#item-count-${this.sectionId}`);
        if (itemCountEl) itemCountEl.textContent = itemCount;

        const discountEl = this.querySelector(`#total-discount-${this.sectionId}`);
        if (discountEl) discountEl.textContent = this.formatMoney(discount);

        const totalEl = this.querySelector(`#total-price-${this.sectionId}`);
        if (totalEl) {
             if (discount > 0) {
                 totalEl.innerHTML = `<span class="original-price-crossed" style="text-decoration: line-through; opacity: 0.7;">${this.formatMoney(subtotal)}</span> <span class="discounted-price">${this.formatMoney(total)}</span>`;
             } else {
                 totalEl.textContent = this.formatMoney(total);
             }
        }

        this.updateDiscountProgress();
        this.updateCartItemsList();
        
        // Update Button State
        const nextButton = this.querySelector(`#bundle-next-${this.sectionId}`);
        if (nextButton) {
          nextButton.disabled = itemCount === 0;
          nextButton.textContent = nextButton.dataset.checkoutText || 'Checkout';
        }
      }

      updateCartItemsList() {
        const container = this.querySelector(`#cart-items-${this.sectionId}`);
        if (!container) return;

        const items = Object.values(this.cart);
        if (items.length === 0) {
          container.innerHTML = `<p class="cart-empty-message">${this.sectionId ? '' : 'Your box is empty'}</p>`; // Fallback text
          return;
        }

        let html = '';
        items.forEach(item => {
           html += `
             <div class="cart-item">
               <div class="cart-item-image-wrapper">
                 <img src="${item.productImage}" class="cart-item-image" alt="">
                 <button class="cart-item-remove" data-product-id="${item.productId}" type="button" aria-label="Remove item">&times;</button>
               </div>
               <div class="cart-item-details">
                 <div class="cart-item-name">${item.productTitle}</div>
                 <div class="cart-item-meta">${this.formatMoney(item.price)} x ${item.quantity}</div>
               </div>
             </div>
           `;
        });
        container.innerHTML = html;
      }
      
      updateDiscountProgress() {
        const itemCount = this.getTotalItemCount();
        const progress = this.querySelector('.discount-progress-bar');
        const track = this.querySelector('.discount-track');
        
        if (!progress || !track) return;

        const circles = this.querySelectorAll('.discount-circle');
        
        // 1. Position 10 circles evenly from 10% to 100%
        circles.forEach(circle => {
            const val = parseInt(circle.dataset.tier);
            const percent = (val / 10) * 100;
            circle.style.left = `${percent}%`;

            // Fill circle logic
            const cappedItemCount = Math.min(itemCount, 10);
            if (itemCount >= val) {
                circle.classList.add('filled');
                // Highlight the most recently reached milestone or the current progress
                if (val === cappedItemCount && itemCount > 0) {
                    circle.classList.add('active');
                } else {
                    circle.classList.remove('active');
                }
            } else {
                circle.classList.remove('filled');
                circle.classList.remove('active');
            }
        });

        // 2. Position the labels (tiers) under the correct circles
        const tiers = this.querySelectorAll('.discount-tier');
        
        tiers.forEach(tier => {
            const tierValue = parseInt(tier.dataset.tier);
            const percent = (tierValue / 10) * 100;
            
            tier.style.position = 'absolute';
            tier.style.left = `${percent}%`;
            tier.style.transform = 'translateX(-50%)';

            if (itemCount >= tierValue) {
                tier.classList.add('active');
            } else {
                tier.classList.remove('active');
            }
        });

        // 3. Calculate Bar Width - each item is 10%
        const width = Math.min(itemCount * 10, 100);
        progress.style.width = `${width}%`;
        progress.style.opacity = '1';
      }

      handleNextClick() {
        this.checkout();
      }

      async checkout() {
        const btn = this.querySelector(`#bundle-next-${this.sectionId}`);
        btn.textContent = 'İşleniyor...';
        btn.disabled = true;
        
        // Sync current bundle state to main cart first, then go checkout
        try {
            await this.syncMainCartWithBundle();

            // Redirect to Checkout with Discount
            const discountCode = this.getCheckoutDiscountQuery();
            let checkoutUrl = '/checkout';
            if (discountCode) {
                checkoutUrl += `?discount=${encodeURIComponent(discountCode)}`;
            }
            window.location.href = checkoutUrl;

        } catch (e) {
            console.error(e);
            alert('Error proceeding to checkout');
            btn.disabled = false;
        }
      }

      formatMoney(cents) {
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(cents / 100);
      }
    }
  );
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', class QuantityInput extends HTMLElement {
     // ... existing quantity input logic or simplified ...
     constructor() {
         super();
         const input = this.querySelector('input');
         this.querySelectorAll('button').forEach(btn => {
             btn.addEventListener('click', () => {
                 const step = btn.name === 'plus' ? 1 : -1;
                 const val = parseInt(input.value) || 0;
                 if (val + step >= 0) {
                     input.value = val + step;
                     input.dispatchEvent(new Event('change', { bubbles: true }));
                 }
             });
         });
     }
  });
}
